<template> 
    <v-card class="chart">
        <v-card-title>
            <v-btn class="mr-2" small @click="showFlag=!showFlag">
            <v-icon v-if="showFlag">mdi-chevron-down</v-icon>
            <v-icon v-else>mdi-chevron-right</v-icon>
            </v-btn>
            <div :style="`color:${isDarkMode ? 'white': 'black'}`">Network Status</div>
            <v-spacer />
            <v-tooltip left>
                <template v-slot:activator="{ on }">
                <v-icon class="mr-1" v-on="on" :color="syncConnectionIsOpen ? 'green':'red'">mdi-hubspot</v-icon>
                </template>
                <span v-if="syncConnectionIsOpen">Sync Hub is Connected</span>
                <span v-else>Sync Hub is NOT Connected</span>
            </v-tooltip>
        </v-card-title>
        <v-card-text :style="`display:${showFlag?'':'none'}`">
            <apexchart ref="theChart" type="heatmap" @dataPointSelection="dataPointSelection" :options="chartOptions" :series="series"></apexchart>
        </v-card-text>
    </v-card>
</template>

<script>
// This component uses ApexCharts. See documentation at 
// https://apexcharts.com/docs/vue-charts/

import Router from '../router';
import { mapState } from "vuex";
import signalr from '../signalr/signalr.js';

const tabletState = { connected: 0, disconnected: 2 };

export default {
    data:() => ({
        tablets: [],
        connectedTablets: [],
        showFlag: true,
        syncConnection: null,
        syncConnectionIsOpen: false,
        series: [],
        chartOptions: {
            theme: {
               // mode: 'dark'
            },
            chart: {
                //height: 350,
                height:600,
                type: 'heatmap',
                zoom: { enabled: false}
            },
            plotOptions: {
                heatmap: {
                    // shadeIntensity: 0.5,
                    // radius: 0,
                    // useFillColorAsStroke: false,
                    colorScale: {
                       // this will be filled in dynamically
                    }
                }
            },
            dataLabels: {
                enabled: true,
            },
            labels: [],
            tooltip: {
            },
            stroke: {
                width: 1
            },
            title: {
                text: ''
            },
        },
    }),
    async created(){
        await this.initializeChart();
        await this.openSyncConnection();    
        this.connectedTablets = await this.syncConnection.invoke('GetNetworkSummary');
        this.chartOptions.theme.mode = this.isDarkMode ? 'dark' : '';
    },
    async beforeDestroy() {
        if (this.syncConnectionIsOpen) {
            await this.syncConnection.send('RemoveFromDashboardGroup');
            await this.closeSyncConnection();
        }
    },

    computed: {
        ...mapState(["settings", "tabletInfo"]),
        theChart() {
            return this.$refs.theChart;
        },
        isDarkMode() {
            var mode = this.settings.isDarkMode;
            return mode;
        }
    },
    watch:{
        connectedTablets:{
        async handler(newVal, oldValue) {
            if(newVal === oldValue) return;
            await this.initializeChart();
            await this.updateChart(newVal);  
            this.theChart.updateOptions(this.chartOptions);
            this.theChart.updateSeries(this.series);
        },
        deep: true
        }
    },
    methods: {
        async initializeChart() {
            this.series = [];
            this.chartOptions.labels = [];

            this.tablets = this.tabletInfo;

            if(this.tablets === null || this.tablets === undefined) return;

            this.chartOptions.plotOptions.heatmap.colorScale.ranges =  [
                { from: 0, to: 0, name: 'Connected',  color: '#05C840' },
                { from: 2, to: 2, name: 'Disconnected', color: '#ff9807' }
            ];



            var tsTabletCnt = this.tablets.length;
            if (tsTabletCnt == 0) return;

            this.chartOptions.tooltip.custom = this.tooltipFormatter;
            this.chartOptions.dataLabels.formatter = this.dataLabelFormatter;

            var farmNames = [this.tablets[0].farmCode];
            var tabletNames = [];
            var allTablets = [];
            var innerArrayValue = [];
            var innerArrayName = [];
            var latestFarmCode = farmNames[0];

            for (var i=0; i< tsTabletCnt; i++) {
                var metric = tabletState.disconnected;

                if (this.tablets[i].farmCode != latestFarmCode) {
                    allTablets.push(innerArrayValue);
                    tabletNames.push(innerArrayName);
                    innerArrayValue = [];
                    innerArrayName = [];
                    latestFarmCode = this.tablets[i].farmCode;
                    farmNames.push(latestFarmCode);
                }

                innerArrayValue.push(metric);
                innerArrayName.push(this.tablets[i].name);
            }
            if (innerArrayValue.length > 0) {
                allTablets.push(innerArrayValue);
                tabletNames.push(innerArrayName);
            }

            this.chartOptions.tooltip.tabletNames = tabletNames;

            var maxFarmTabletCnt = 0;
            farmNames.forEach((name, index) => {
                var farmTabletCnt = allTablets[index].length;
                maxFarmTabletCnt =  farmTabletCnt > maxFarmTabletCnt ? farmTabletCnt : maxFarmTabletCnt;
                this.series.push( { name: name, data: allTablets[index] });
            });
            for (var j=0; j< maxFarmTabletCnt; j++) this.chartOptions.labels.push(j+1);
        },

        async openSyncConnection() {
            this.syncConnection = await signalr.buildHub();

            try {
                this.syncConnection.on('TabletConnected', 
                    tabletName => {
                        // console.log(`${tabletName} is CONNECTED.`);
                        this.updateChart([{tabletName: tabletName, isOpen: true}]);
                    });

                this.syncConnection.on('TabletDisconnected', 
                    tabletName => {
                        // console.log(`${tabletName} is DISCONNECTED.`);
                        this.updateChart([{tabletName: tabletName, isOpen: false}]);
                    });
                
                this.syncConnection.on('ClientConnected', () => {}); // Ignore
                this.syncConnection.on('ClientDisconnected', () => {}); // Ignore
                this.syncConnection.on('SyncProgress', () => {}); // Ignore
                this.syncConnection.on('SyncQueued', () => {}); // Ignore
                this.syncConnection.on('SyncStarted', () => {}); // Ignore 
                this.syncConnection.on('SyncCompleted', () => {}); // Ignore 

                this.syncConnection.onclose(() => {
                    this.syncConnectionIsOpen = false;
                });

                await this.syncConnection.start();
                this.syncConnectionIsOpen = true;
                await this.syncConnection.send('AddToDashboardGroup');
            }
            catch (err) {
                this.$root.showNoticeError(`Connection to Sync Hub failed: ${err}`);
                this.syncConnectionIsOpen = false;
            }
        },

        async closeSyncConnection() {
            await this.syncConnection.stop();
            this.syncConnectionIsOpen = false;
        },

        updateChart(connectedTablets) {
            var chart = this.theChart;

            //* not sure why these are not setup sometimes. Weird. 
            if (this.chartOptions.tooltip.tabletNames == null) return;
            if (chart == null) return;
            //* 
           
            var numFarms = this.chartOptions.tooltip.tabletNames.length;

            for (var i=0; i<numFarms; i++)
            {
                var numFarmTablets = this.chartOptions.tooltip.tabletNames[i].length;
                for (var j=0; j<numFarmTablets; j++)
                {
                    var tabletName = this.chartOptions.tooltip.tabletNames[i][j];
                    var connTablet = connectedTablets.find(x=>x.tabletName === tabletName);
                    if (connTablet) {
                        if (connTablet.isOpen) this.series[i].data[j] = tabletState.connected;
                        else this.series[i].data[j] = tabletState.disconnected;
                    }
                }
            }
        },

        dataPointSelection: (event, chartContext, selected) => {
            var name = selected.w.config.tooltip.tabletNames[selected.seriesIndex][selected.dataPointIndex];
            Router.push({name:'TabletDetail',query:{name:name}})
        },

        tooltipFormatter({series, seriesIndex, dataPointIndex, w}) {
            var tabletName = w.config.tooltip.tabletNames[seriesIndex][dataPointIndex];
            var desc;
            switch(series[seriesIndex][dataPointIndex]) {
                case 0: desc = "Connected"; break;
                default:desc = "Disconnected";
            }

            var color = this.isDarkMode ? '#dddddd': '#1e1e1e';
            return `<div style="color: ${color}; padding:5px">` +
                '<span>' + tabletName + ": " + desc + '</span>' +
                '</div>';
        },

        dataLabelFormatter(val) {
            if (val === 0) return "C";
            else if (val === 1)  return "S";
            else return "D";
        }
    }
}
</script>
<style scoped>
    .chart {
      max-width: 600px; 
        min-width:600px;
      color:white;
    }
</style>